/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Dither Node
   High-performance GPU ordered matrix (Bayer 4x4, 8x8) and noise dithering.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER, GLSL_COLOR_UTILS, GLSL_BAYER_MATRICES, GLSL_NOISE_UTILS } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const DITHER_SHADER = `${GLSL_HEADER}
${GLSL_COLOR_UTILS}
${GLSL_NOISE_UTILS}
${GLSL_BAYER_MATRICES}

uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_scale;
uniform float u_levels;
uniform int u_matrixType; // 0 = Bayer 4x4, 1 = Bayer 8x8, 2 = Random Noise
uniform bool u_colorMode;  // false = Monochrome, true = Color
uniform float u_seed;

void main() {
  vec4 src = texture(u_image, v_uv);
  vec2 pixelCoord = (v_uv * u_resolution) / max(u_scale, 1.0);
  
  float threshold = 0.5;
  if (u_matrixType == 0) {
    threshold = bayer4(pixelCoord);
  } else if (u_matrixType == 1) {
    threshold = bayer8(pixelCoord);
  } else if (u_matrixType == 2) {
    threshold = hash21(floor(pixelCoord), u_seed);
  }

  // Adjust threshold spread around 0 [-0.5 .. 0.5]
  float spread = (threshold - 0.5) / max(u_levels, 1.0);

  if (!u_colorMode) {
    // Monochrome luminance dithering
    float lum = getLuminance(src.rgb);
    float ditheredLum = clamp(lum + spread, 0.0, 1.0);
    float quantized = floor(ditheredLum * (u_levels - 1.0) + 0.5) / (u_levels - 1.0);
    fragColor = vec4(vec3(quantized), src.a);
  } else {
    // RGB channel dithering
    vec3 dithered = clamp(src.rgb + vec3(spread), 0.0, 1.0);
    vec3 quantized = floor(dithered * (u_levels - 1.0) + 0.5) / (u_levels - 1.0);
    fragColor = vec4(quantized, src.a);
  }
}
`;

export const ditherNode: NodeDefinition = {
  type: 'filter.dither',
  category: 'Filter',
  label: 'Dither',
  description: 'Applies ordered Bayer matrix or noise dithering with color and monochrome modes',
  keywords: ['dither', 'bayer', 'ordered', 'retro', 'pixel', '1bit', 'monochrome', 'matrix'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Dithered', type: 'image' },
  ],
  params: [
    {
      id: 'pattern',
      name: 'Dither Pattern',
      type: 'select',
      defaultValue: 'bayer8',
      options: [
        { label: 'Bayer 8x8 (Smooth)', value: 'bayer8' },
        { label: 'Bayer 4x4 (Classic)', value: 'bayer4' },
        { label: 'Random Threshold', value: 'noise' },
      ],
    },
    {
      id: 'scale',
      name: 'Pattern Scale',
      type: 'slider',
      defaultValue: 2,
      min: 1,
      max: 16,
      step: 1,
      unit: 'px',
    },
    {
      id: 'levels',
      name: 'Quantize Levels',
      type: 'slider',
      defaultValue: 2,
      min: 2,
      max: 16,
      step: 1,
    },
    {
      id: 'colorMode',
      name: 'Color Dithering',
      type: 'boolean',
      defaultValue: false,
      uiHint: 'toggle',
    },
    {
      id: 'seed',
      name: 'Noise Seed',
      type: 'number',
      defaultValue: 42,
      min: 0,
      max: 99999,
      step: 1,
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: DITHER_SHADER,
    getUniforms: (inputs, params, dimensions) => {
      const patternStr = String(params.pattern ?? 'bayer8');
      let patternInt = 1;
      if (patternStr === 'bayer4') patternInt = 0;
      else if (patternStr === 'noise') patternInt = 2;

      return {
        u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
        u_resolution: [dimensions.width, dimensions.height],
        u_scale: Number(params.scale) || 2,
        u_levels: Number(params.levels) || 2,
        u_matrixType: patternInt,
        u_colorMode: Boolean(params.colorMode),
        u_seed: Number(params.seed) || 42,
      };
    },
  }),
};
